import { config } from 'config/config';
import routePaths from 'config/routes';
import getConfig from 'next/config';
import apiPaths from 'services/apiPaths';
import redirectTo from 'services/redirectTo';
import {
  fetchServerAppActions,
  fetchServerUserMenu,
} from 'src/actions/appActions';
import {
  logout,
  _reAuthenticate,
  _setInitialUserInfo,
} from 'src/actions/authActions';
import { IRootReducers } from 'src/reducers';
import { UserStatusEnum } from 'src/shared/enums';
import { IUser } from 'src/shared/models';
import { getCookie, getUserStatus } from 'utils';

const { publicRuntimeConfig } = getConfig();
const {
  API: { BASE_URL },
  COOKIES: { ACCESS_TOKEN, REFRESH_TOKEN },
} = config;

const {
  PAGES: { LOGIN },
} = routePaths;

// -------------- UTILS --------------

export function getClientSideAuthInfo(appState: IRootReducers) {
  const user = appState.auth.user;
  const accessToken = appState.auth.accessToken;

  return { user, accessToken };
}

const isUserStatusValid = (user: IUser) => {
  return getUserStatus(user) !== UserStatusEnum.DENIED;
};

const fetchUserInitialData = async (
  accessToken: string,
  refreshToken: string,
  ctx: any,
  user: any
) => {
  try {
    const userPoints = user?.points;

    await fetchServerUserMenu(accessToken, ctx);
    await fetchServerAppActions(accessToken, ctx);

    await ctx.store.dispatch(
      _setInitialUserInfo({
        user,
        userPoints,
        accessToken,
        refreshToken,
      })
    );
  } catch (err) {
    console.error(err);
    ctx.store.dispatch(logout(ctx));
  }
};

const processUserData = async (
  user: IUser,
  accessToken: string,
  refreshToken: string,
  ctx: any,
  logout: () => Promise<boolean>
) => {
  if (!isUserStatusValid(user)) {
    await logout();
    return false;
  }

  await fetchUserInitialData(accessToken, refreshToken, ctx, user);
  return true;
};

const getUserInfoCall = async (token: string) => {
  return await fetch(`${BASE_URL}${apiPaths.CALL.USER_INFO}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

// -------------- END UTILS --------------

export async function getServerSideAuthInfo(ctx: any, isPublicRoute: boolean) {
  let user: IUser;
  let accessToken = getCookie(ACCESS_TOKEN, ctx.req);
  let refreshToken = getCookie(REFRESH_TOKEN, ctx.req);
  let isUserAuthenticated = Boolean(accessToken && refreshToken);

  // Refresh access-token
  if (!accessToken && refreshToken) {
    const newCredentials = await _reAuthenticate(ctx);
    if (!newCredentials) return;

    accessToken = newCredentials.newAccessToken;
    refreshToken = newCredentials.newRefreshToken;
    isUserAuthenticated = true;
  }

  const serverSideLogout = async () => ctx.store.dispatch(logout(ctx));

  if (!isUserAuthenticated && isPublicRoute) return {};

  if (!isUserAuthenticated && !isPublicRoute) {
    let redirectUrl = LOGIN;

    if (ctx.req.url) {
      const cleanOriginUrl = (ctx.req?.url ?? ctx.pathname).replace(
        publicRuntimeConfig.PLATFORM_PREFIX,
        ''
      );
      const searchParams = new URLSearchParams(cleanOriginUrl.split('?')[1]);
      redirectUrl += `?redirect=${encodeURIComponent(
        cleanOriginUrl,
      )}&${searchParams}`;
    }

    redirectTo(redirectUrl, ctx);
    return;
  }

  try {
    const res = await getUserInfoCall(accessToken);

    switch (res.status) {
      case 200:
        user = await res.json();

        const userProcessed = await processUserData(
          user,
          accessToken,
          refreshToken,
          ctx,
          serverSideLogout
        );

        if (!userProcessed) {
          return;
        }

        return { user, accessToken };

      case 401:
        const reAuthentication = await _reAuthenticate(ctx);

        if (!reAuthentication) {
          // redirection made in _reAuthenticate
          return;
        }

        const { newAccessToken, newRefreshToken } = reAuthentication;

        const refreshUserInfoCall = await getUserInfoCall(
          reAuthentication.newAccessToken
        );

        if (refreshUserInfoCall.status !== 200) {
          await serverSideLogout();
          return;
        }

        user = await refreshUserInfoCall.json();

        const userReProcessed = await processUserData(
          user,
          newAccessToken,
          newRefreshToken,
          ctx,
          serverSideLogout
        );

        if (!userReProcessed) {
          return;
        }

        return { user, accessToken: newAccessToken };

      default:
        await serverSideLogout();
        return;
    }
  } catch (err) {
    await serverSideLogout();
    return;
  }
}
